import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Building2, Check, Truck } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import api, { formatApiErrorDetail, postWithRetry } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { HERO_IMAGES } from "@/lib/constants";

function Field({ label, required = false, children }) {
  return (
    <div>
      <label className="label-brox">
        {label}
        {required && <span className="text-copper"> *</span>}
      </label>
      {children}
    </div>
  );
}

function PartnerForm() {
  const { config } = useSite();
  const [form, setForm] = useState({
    company: "", contact_person: "", phone: "", email: "", county_city: "", areas_covered: "",
    work_types: "", workers_count: "", experience_years: "", company_details: "", availability: "",
    additional_info: "", consent: false,
  });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await postWithRetry("/partners/apply", form);
      setSent(true);
    } catch (err) {
      setError(err.response
        ? formatApiErrorDetail(err.response.data?.detail) || err.message
        : "Nu am reușit să trimitem cererea. Verifică conexiunea la internet și încearcă din nou.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center border border-copper/40 bg-copper/5 p-10 text-center" data-testid="partner-form-success">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-copper text-ink-950"><Check className="h-7 w-7" strokeWidth={3} /></span>
        <h3 className="display-title mt-5 text-3xl">Cerere trimisă</h3>
        <p className="mt-3 max-w-sm text-sm text-muted-foreground">
          Cererea ta de parteneriat a fost înregistrată. Te contactăm pentru următorii pași.
        </p>
        {config.email && (
          <p className="mt-2 text-xs text-muted-foreground">
            Preferi email?{" "}
            <a href={`mailto:${config.email}`} className="text-copper underline underline-offset-2" data-testid="partner-success-email">
              {config.email}
            </a>
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="partner-application-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Denumirea firmei / echipei" required>
          <input required className="input-brox" value={form.company} onChange={set("company")} data-testid="partner-company-input" />
        </Field>
        <Field label="Persoana de contact" required>
          <input required className="input-brox" value={form.contact_person} onChange={set("contact_person")} data-testid="partner-contact-input" />
        </Field>
        <Field label="Telefon" required>
          <input required type="tel" className="input-brox" value={form.phone} onChange={set("phone")} data-testid="partner-phone-input" />
        </Field>
        <Field label="Email" required>
          <input required type="email" className="input-brox" value={form.email} onChange={set("email")} data-testid="partner-email-input" />
        </Field>
        <Field label="Județ / oraș de reședință">
          <input className="input-brox" value={form.county_city} onChange={set("county_city")} data-testid="partner-county-input" />
        </Field>
        <Field label="Zone acoperite (judete / raza km)">
          <input className="input-brox" value={form.areas_covered} onChange={set("areas_covered")} data-testid="partner-areas-input" />
        </Field>
        <Field label="Tipuri de lucrări / servicii">
          <input className="input-brox" placeholder="Ex: zidărie, glet, instalații electrice" value={form.work_types} onChange={set("work_types")} data-testid="partner-worktypes-input" />
        </Field>
        <Field label="Număr de lucrători">
          <input className="input-brox" value={form.workers_count} onChange={set("workers_count")} data-testid="partner-workers-input" />
        </Field>
        <Field label="Ani de experiență">
          <input className="input-brox" value={form.experience_years} onChange={set("experience_years")} data-testid="partner-experience-input" />
        </Field>
        <Field label="Detalii firmă (CUI, autorizații)">
          <input className="input-brox" value={form.company_details} onChange={set("company_details")} data-testid="partner-details-input" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Disponibilitate (începere proiect)">
            <input className="input-brox" placeholder="Ex: imediat, din luna viitoare" value={form.availability} onChange={set("availability")} data-testid="partner-availability-input" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Informații suplimentare">
            <textarea className="input-brox min-h-[110px] resize-none" value={form.additional_info} onChange={set("additional_info")} data-testid="partner-additional-input" />
          </Field>
        </div>
      </div>

      <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-muted-foreground">
        <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-[#D96B27]" checked={form.consent} onChange={set("consent")} required data-testid="partner-consent-checkbox" />
        <span>
          Sunt de acord cu prelucrarea datelor personale din această cerere, conform{" "}
          <a href="/politica-confidentialitate" className="text-copper underline underline-offset-2">Politicii de confidențialitate</a>.
        </span>
      </label>

      {error && <p className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400" data-testid="partner-form-error">{error}</p>}

      <button type="submit" disabled={busy} className={`btn-primary w-full ${busy ? "cursor-wait opacity-70" : ""}`} data-testid="partner-submit-btn">
        {busy ? "SE TRIMITE..." : "TRIMITE CEREREA DE PARTENERIAT"}
      </button>
    </form>
  );
}

function SupplierForm() {
  const { config } = useSite();
  const [form, setForm] = useState({
    company: "", contact_person: "", phone: "", email: "", locations: "",
    material_categories: "", delivery_areas: "", additional_info: "", consent: false,
  });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await postWithRetry("/suppliers/apply", form);
      setSent(true);
    } catch (err) {
      setError(err.response
        ? formatApiErrorDetail(err.response.data?.detail) || err.message
        : "Nu am reușit să trimitem cererea. Verifică conexiunea la internet și încearcă din nou.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center border border-copper/40 bg-copper/5 p-10 text-center" data-testid="supplier-form-success">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-copper text-ink-950"><Check className="h-7 w-7" strokeWidth={3} /></span>
        <h3 className="display-title mt-5 text-3xl">Cerere trimisă</h3>
        <p className="mt-3 max-w-sm text-sm text-muted-foreground">
          Cererea ta de parteneriat pentru furnizare a fost înregistrată. Te contactăm pentru următorii pași.
        </p>
        {config.email && (
          <p className="mt-2 text-xs text-muted-foreground">
            Preferi email?{" "}
            <a href={`mailto:${config.email}`} className="text-copper underline underline-offset-2" data-testid="supplier-success-email">
              {config.email}
            </a>
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="supplier-application-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Compania" required>
          <input required className="input-brox" value={form.company} onChange={set("company")} data-testid="supplier-company-input" />
        </Field>
        <Field label="Persoana de contact" required>
          <input required className="input-brox" value={form.contact_person} onChange={set("contact_person")} data-testid="supplier-contact-input" />
        </Field>
        <Field label="Telefon" required>
          <input required type="tel" className="input-brox" value={form.phone} onChange={set("phone")} data-testid="supplier-phone-input" />
        </Field>
        <Field label="Email" required>
          <input required type="email" className="input-brox" value={form.email} onChange={set("email")} data-testid="supplier-email-input" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Locațiile / depozitele">
            <input className="input-brox" value={form.locations} onChange={set("locations")} data-testid="supplier-locations-input" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Categorii de materiale">
            <input className="input-brox" placeholder="Ex: rigips, gresie, vopsea, parchet" value={form.material_categories} onChange={set("material_categories")} data-testid="supplier-categories-input" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Zone de livrare">
            <input className="input-brox" value={form.delivery_areas} onChange={set("delivery_areas")} data-testid="supplier-delivery-input" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Informații suplimentare">
            <textarea className="input-brox min-h-[110px] resize-none" value={form.additional_info} onChange={set("additional_info")} data-testid="supplier-additional-input" />
          </Field>
        </div>
      </div>

      <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-muted-foreground">
        <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-[#D96B27]" checked={form.consent} onChange={set("consent")} required data-testid="supplier-consent-checkbox" />
        <span>
          Sunt de acord cu prelucrarea datelor personale din această cerere, conform{" "}
          <a href="/politica-confidentialitate" className="text-copper underline underline-offset-2">Politicii de confidențialitate</a>.
        </span>
      </label>

      {error && <p className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400" data-testid="supplier-form-error">{error}</p>}

      <button type="submit" disabled={busy} className={`btn-primary w-full ${busy ? "cursor-wait opacity-70" : ""}`} data-testid="supplier-submit-btn">
        {busy ? "SE TRIMITE..." : "DEVINO FURNIZOR PARTENER"}
      </button>
    </form>
  );
}

export default function Partners() {
  const [tab, setTab] = useState("partner");
  return (
    <>
      <SEO
        title="Devino partener Brox Construct — echipe și furnizori"
        description="Ai o firmă sau o echipă de meseriași? Brox Construct dezvoltă o rețea de profesioniști pentru proiectele primite prin platformă. Aplică ca partener executant sau furnizor de materiale."
        path="/parteneri"
        image={HERO_IMAGES.partners}
      />

      <section className="grain relative overflow-hidden bg-ink-950 pb-10 pt-28 sm:pb-20 sm:pt-40">
        <div className="container-brox grid items-end gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow mb-5">REȚEAUA BROX</p></Reveal>
            <Reveal delay={0.08}>
              <h1 className="display-title text-4xl sm:text-6xl lg:text-7xl" data-testid="partners-title">
                Ai o firmă sau o echipă<br />de meseriași?
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg" data-testid="partners-copy">
                Brox Construct dezvoltă o rețea de profesioniști către care putem distribui proiectele primite,
                în funcție de specializare, zonă și disponibilitate. Lucrăm cu echipe care țin la calitate și la
                termene.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <img src={HERO_IMAGES.partners} alt="Echipă de meseriași pe șantier" loading="lazy" className="h-56 w-full object-cover sm:h-72" data-testid="partners-image" />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-ink-900 py-10 sm:py-16" data-testid="partners-benefits">
        <div className="container-brox grid gap-6 sm:grid-cols-3">
          {[
            "Cereri de proiecte potrivite specializării și zonei tale, fără costuri de achiziție marketing.",
            "Plată transparentă și condiții stabilite înainte de start.",
            "Coordonare profesionistă pe șantier — tu te ocupi de execuție.",
          ].map((b) => (
            <Reveal key={b}>
              <div className="flex items-start gap-3 border border-white/10 bg-ink-950 p-6">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-copper" strokeWidth={3} />
                <p className="text-sm leading-relaxed text-paper/85">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ink-950 py-12 sm:py-24" data-testid="partners-form-section">
        <div className="container-brox max-w-4xl">
          <div className="mb-10 grid grid-cols-2 gap-2" role="tablist" data-testid="partners-tabs">
            {[
              { id: "partner", label: "PARTENER EXECUȚIE", icon: <Building2 className="h-4 w-4" />, testid: "partners-tab-executie" },
              { id: "supplier", label: "FURNIZOR MATERIALE", icon: <Truck className="h-4 w-4" />, testid: "partners-tab-furnizor" },
            ].map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`flex min-h-[52px] items-center justify-center gap-2 border font-display text-sm font-bold uppercase tracking-wider transition-all sm:text-base ${
                  tab === t.id ? "border-copper bg-copper text-ink-950" : "border-white/15 text-paper/70 hover:border-copper/60 hover:text-copper"
                }`}
                data-testid={t.testid}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.25 }}>
              {tab === "partner" ? (
                <div>
                  <h2 className="display-title mb-2 text-3xl sm:text-4xl">Cerere de parteneriat — execuție</h2>
                  <p className="mb-8 text-sm text-muted-foreground">Completează câmpurile de mai jos. Îți răspundem după analiza cererii.</p>
                  <PartnerForm />
                </div>
              ) : (
                <div>
                  <h2 className="display-title mb-2 text-3xl sm:text-4xl">Ești furnizor de materiale?</h2>
                  <p className="mb-8 text-sm text-muted-foreground">Intră în rețeaua de furnizori Brox Construct. Completează cererea de mai jos.</p>
                  <SupplierForm />
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </>
  );
}
